import { Button } from '@caterinx/ui/components/ui/button';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { Check, Loader2, Upload } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { uploadFile, type UploadKind } from '../lib/supplier.js';

/**
 * Ein Datei-Feld, das direkt nach S3 hochlädt und den Objektschlüssel zurückgibt.
 *
 * Der Upload läuft **am Backend vorbei** über eine vorsignierte URL. Der Grund ist nicht
 * Eleganz, sondern das 6-MB-Payload-Limit von API Gateway: Ein mit dem Handy abfotografierter
 * Gewerbeschein liegt regelmäßig darüber, und der Fehler dabei sieht für den Nutzer aus
 * wie ein kaputtes Formular.
 *
 * Der Schlüssel wird **nicht** sofort gespeichert. Erst der umgebende Schritt schreibt ihn
 * ins Profil — ein abgebrochener Upload hinterlässt dadurch kein halbes Item, sondern nur ein
 * verwaistes S3-Objekt, das die Lifecycle-Regel des Buckets einsammelt.
 */
export function FileField({
  kind,
  label,
  hint,
  accept,
  currentUrl,
  onUploaded,
}: {
  kind: UploadKind;
  label: string;
  hint?: string;
  accept: string;
  /** Vorsignierter Link auf die bereits hinterlegte Datei, falls vorhanden. */
  currentUrl: string | null;
  onUploaded: (s3Key: string) => void;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [justUploaded, setJustUploaded] = useState(false);

  const handleChange = async (): Promise<void> => {
    const file = inputRef.current?.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const s3Key = await uploadFile(kind, file);
      onUploaded(s3Key);
      setJustUploaded(true);
      toast({ title: 'Datei hochgeladen', description: file.name });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Upload fehlgeschlagen',
        description: 'Bitte prüfen Sie Dateityp und Größe und versuchen Sie es erneut.',
      });
    } finally {
      setIsUploading(false);
      // Zurücksetzen, damit dieselbe Datei nach einem Fehlschlag erneut gewählt werden kann —
      // ohne das feuert `change` beim zweiten Mal nicht.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={inputId} className="text-sm font-medium">
          {label}
        </label>
        {(currentUrl || justUploaded) && (
          <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            hinterlegt
          </span>
        )}
      </div>

      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={() => void handleChange()}
        />
        <Button
          type="button"
          variant="outline"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
          className="min-h-11"
        >
          {isUploading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          {currentUrl || justUploaded ? 'Ersetzen' : 'Datei wählen'}
        </Button>

        {currentUrl && (
          /**
           * Vorsignierter Link mit kurzer Laufzeit. `rel="noreferrer"` ist hier kein Ritual:
           * Die URL trägt die Signatur im Query-String, und ohne das Attribut stünde sie im
           * `Referer` jeder Seite, die das Ziel weiterverlinkt.
           */
          <a
            href={currentUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary text-sm underline underline-offset-4"
          >
            Hinterlegte Datei ansehen
          </a>
        )}
      </div>
    </div>
  );
}
